import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { replaceOrAppend } from 'radash';
import { map, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import {
  Lesson,
  LessonStatus,
  NotesType,
} from '../../../../models/lesson.school.model';
import { SchoolMemberType } from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import { UserModel } from '../../../../models/user.model';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { AuthService } from '../../../../services/auth.service';
import { SchoolLessonService } from '../../../../services/lesson.school.service';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { UtilsService } from '../../../../services/utils.service';
import * as lessonActions from '../../../../state/actions/lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnChipComponent } from '../../../components/buttons/btn-chip/btn-chip.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { BtnRouteComponent } from '../../../components/buttons/btn-route/btn-route.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { BtnTextComponent } from '../../../components/buttons/btn-text/btn-text.component';
import {
  AlertType,
  DlgConfirmComponent,
} from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { ListUtilsBarComponent } from '../../../components/list-utils-bar/list-utils-bar.component';
import { DlgAddToGradeComponent } from '../dlg-add-to-grade/dlg-add-to-grade.component';
import { DlgLessonsFiltersComponent } from '../dlg-lessons-filters/dlg-lessons-filters.component';
import { DlgUpsertLessonComponent } from '../dlg-upsert-lesson/dlg-upsert-lesson.component';

@Component({
  selector: 'app-list-lessons',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnRouteComponent,
    BtnSubmitComponent,
    BtnTextComponent,
    NgTemplateOutlet,
    BtnChipComponent,
    BtnFabComponent,
    BtnMoreComponent,
    ReactiveFormsModule,
    RouterLink,
    AsyncPipe,
    MomentPipe,
    ListUtilsBarComponent,
    BtnSubmitComponent,
    BtnActionComponent,
  ],
  templateUrl: './list-lessons.component.html',
  styleUrl: './list-lessons.component.css',
})
export class ListLessonsComponent implements OnInit, AfterViewInit, OnDestroy {
  activeDropdownLessonId: string | null = null;
  @ViewChild('btnDropdownRef', { static: false }) btnDropdownRef!: ElementRef;
  @ViewChild('popoverDropdownRef', { static: false })
  popoverDropdownRef: ElementRef | undefined;

  firstNewItemId: string | null = null;
  private cdr = inject(ChangeDetectorRef);
  @ViewChildren('itemRef') itemRefs!: QueryList<ElementRef>;
  @ViewChild(ListUtilsBarComponent) listUtilsBar!: ListUtilsBarComponent;

  advSearchForm: FormGroup;

  route: ActivatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  logger: NGXLogger = inject(NGXLogger);
  roles: SchoolRolesService = inject(SchoolRolesService);
  toastr: ToastrService = inject(ToastrService);
  private dialog: MatDialog = inject(MatDialog);
  authService: AuthService = inject(AuthService);
  lessonService: SchoolLessonService = inject(SchoolLessonService);
  utilsService: UtilsService = inject(UtilsService);

  user: UserModel | null = null;

  grades$: Observable<Grade[] | undefined> = new Observable();
  displayedLessons: Lesson[] | undefined;
  lessons: Lesson[] | undefined;
  searchedLessons: Lesson[] | undefined;
  group: Group | null | undefined;

  loadingLessons$: Observable<boolean> = new Observable();
  isSearching$: Observable<boolean> = new Observable();
  showingSearchResults$: Observable<boolean> = new Observable();
  isShowingSearchResults = false;

  hasMoreLessons = true;
  hasMoreSearchResults = false;
  isLoadingMore = false;
  isLoadingMore$: Observable<boolean> = of(false);
  readonly searchPageSize = 25;

  activeSearchParams: {
    searchTerm: string;
    filterFields?: Record<string, any>;
    rangeFields?: any[];
  } | null = null;

  searchFilter: string | null = null;
  searchGrades: Set<string> = new Set();
  availableGrades: Grade[] = [];

  groupId: string | null = this.route.snapshot.paramMap.get('id');

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;
  rolesType = SchoolRoles;
  lessonStatusType = LessonStatus;

  myRoles$: Observable<any> = new Observable();

  lessonStatus = LessonStatus;
  notesType = NotesType;

  exit$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
  ) {
    this.route.paramMap.pipe(takeUntil(this.exit$)).subscribe((params) => {
      this.groupId = params.get('id');
    });

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
      });

    this.grades$ = this.store.select(
      gradeSelector.getGradesByGroup(this.groupId),
    );

    this.advSearchForm = this.fb.group({
      searchTerm: new FormControl('', [Validators.maxLength(200)]),
      gradeAge: this.fb.group({
        fromAge: new FormControl<number | null>(null, [
          Validators.min(1),
          Validators.max(100),
          Validators.pattern('^[0-9]*$'),
        ]),
        toAge: new FormControl<number | null>(null, [
          Validators.min(1),
          Validators.max(100),
          Validators.pattern('^[0-9]*$'),
        ]),
      }),
      selectedGrades: this.fb.array([]),
    });
    this.advSearchForm.valueChanges
      .pipe(takeUntil(this.exit$))
      .subscribe(() => {
        this.applyLocalFilters();
      });
  }

  private setGradeControls(grades: Grade[]) {
    const formArray = this.advSearchForm.get('selectedGrades') as FormArray;
    formArray.clear();

    // Push a new FormControl (boolean) for every grade received
    grades.forEach(() => {
      formArray.push(new FormControl(false));
    });
  }

  get gradesFormArray() {
    return this.advSearchForm.get('selectedGrades') as FormArray;
  }

  async advSearch() {
    if (!this.groupId) return;
    this.logger.log('Advanced Search: ', this.advSearchForm.value);

    this.displayedLessons = [];
    this.searchedLessons = [];
    this.isSearching$ = of(true);

    try {
      const selectedGradesValues =
        (this.advSearchForm.get('selectedGrades')?.value as boolean[]) || [];
      const selectedGradeIds = this.availableGrades
        .filter((_, index) => selectedGradesValues[index])
        .map((grade) => grade.id!)
        .filter((id): id is string => !!id);

      const filterFields =
        selectedGradeIds.length > 0 ? { gradeId: selectedGradeIds } : undefined;

      const fromAge = this.advSearchForm.get('gradeAge.fromAge')?.value;
      const toAge = this.advSearchForm.get('gradeAge.toAge')?.value;

      const rangeFields: any[] = [];
      const avgAgeRange: any = {};
      if (fromAge !== null && fromAge !== undefined && fromAge !== '') {
        avgAgeRange.gte = Number(fromAge);
      }
      if (toAge !== null && toAge !== undefined && toAge !== '') {
        avgAgeRange.lte = Number(toAge);
      }
      if (Object.keys(avgAgeRange).length > 0) {
        rangeFields.push({ avgAge: avgAgeRange });
      }

      const searchTerm = this.advSearchForm.get('searchTerm')?.value || '';

      this.activeSearchParams = {
        searchTerm,
        filterFields,
        rangeFields: rangeFields.length > 0 ? rangeFields : undefined,
      };

      const result = await this.lessonService.searchLessons(
        searchTerm,
        this.groupId,
        filterFields,
        rangeFields.length > 0 ? rangeFields : undefined,
        this.searchPageSize,
        0,
        this.searchPageSize,
      );
      this.searchedLessons = result;
      this.displayedLessons = result;
      this.hasMoreSearchResults = result.length >= this.searchPageSize;
      this.isSearching$ = of(false);
      this.searchFilter = searchTerm;
      this.showingSearchResults$ = of(true);
      this.isShowingSearchResults = true;

      if (this.listUtilsBar) {
        this.listUtilsBar.searchTerm = this.getFilterSummary();
        this.listUtilsBar.cancelAdvSrch();
      }
    } catch (error: any) {
      this.isSearching$ = of(false);
      this.hasMoreSearchResults = false;
      this.utilsService.onShowAlert({
        title: 'Search Error',
        message:
          error?.message ||
          error?.toString() ||
          'An error occurred during search. Please try again.',
        code: 'ERR_SEARCH_FAILED',
      });
    }
  }

  ngOnInit(): void {
    this.onRefresh();
    this.grades$.subscribe((grades) => {
      if (grades) {
        this.availableGrades = grades;
        this.setGradeControls(grades);
      }
    });
  }

  ngAfterViewInit() {
    this.itemRefs.changes.subscribe(() => {
      if (this.firstNewItemId) {
        const el = this.itemRefs.find(
          (el) =>
            el.nativeElement.getAttribute('data-id') === this.firstNewItemId,
        );
        if (el) {
          el.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });

          requestAnimationFrame(() => {
            this.firstNewItemId = null;
            this.cdr.detectChanges();

            setTimeout(() => {
              this.firstNewItemId = null;
              this.cdr.detectChanges();
            }, 2000);
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  getGradeName(gradeId?: string): Observable<string> {
    if (gradeId == undefined) return of('');
    return this.grades$.pipe(
      take(1),
      map((grades) => {
        const grade = grades?.find((g) => g.id === gradeId);
        return grade?.gradeName != null ? `${grade.gradeName},` : '';
      }),
    );
  }

  addMoreLessons() {
    if (this.groupId === null) return;

    if (this.isShowingSearchResults && this.activeSearchParams) {
      this.addMoreSearchResults();
      return;
    }

    this.isLoadingMore = true;
    this.isLoadingMore$ = of(true);

    this.lessonService
      .getLessons({
        groupId: this.groupId!,
        lastRecord:
          (this.lessons ?? [])?.length > 0
            ? this.lessons![this.lessons!.length - 1]?.modified
            : undefined,
      })
      .subscribe({
        next: (lessons) => {
          this.isLoadingMore = false;
          this.isLoadingMore$ = of(false);
          if (lessons.length > 0) {
            this.firstNewItemId = lessons[0].id ?? null;
            this.lessons = [...(this.lessons ?? []), ...lessons];
            this.displayedLessons = this.lessons;
          }
          this.hasMoreLessons = lessons.length >= 10;
        },
        error: (err) => {
          this.isLoadingMore = false;
          this.isLoadingMore$ = of(false);
          this.logger.error('Error fetching more lessons:', err);
        },
      });
  }

  async addMoreSearchResults() {
    if (!this.groupId || !this.activeSearchParams || this.isLoadingMore) return;

    this.isLoadingMore = true;
    this.isLoadingMore$ = of(true);
    const currentCount = this.displayedLessons?.length ?? 0;

    try {
      const results = await this.lessonService.searchLessons(
        this.activeSearchParams.searchTerm,
        this.groupId,
        this.activeSearchParams.filterFields,
        this.activeSearchParams.rangeFields,
        currentCount + this.searchPageSize,
        currentCount,
        this.searchPageSize,
      );

      const existingIds = new Set(
        (this.displayedLessons ?? []).map((l) => l.id),
      );
      const newLessons = results.filter((l) => !existingIds.has(l.id));

      if (newLessons.length > 0) {
        this.firstNewItemId = newLessons[0].id ?? null;
        this.searchedLessons = [
          ...(this.displayedLessons ?? []),
          ...newLessons,
        ];
        this.displayedLessons = this.searchedLessons;
      }

      this.hasMoreSearchResults = newLessons.length >= this.searchPageSize;
    } catch (error: any) {
      this.logger.error('Error fetching more search results:', error);
      this.utilsService.onShowAlert({
        title: 'Search Error',
        message:
          error?.message ||
          error?.toString() ||
          'An error occurred while loading more lessons. Please try again.',
        code: 'ERR_SEARCH_MORE_FAILED',
      });
    } finally {
      this.isLoadingMore = false;
      this.isLoadingMore$ = of(false);
    }
  }

  async onNewLesson() {
    if (!this.groupId) return;

    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Create Lesson',
      description: `Create a new lesson`,
      action: 'Create',
      groupId: this.groupId,
    };
    const dialogRef = this.dialog.open(DlgUpsertLessonComponent, dialogConfig);
    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success('Lesson created successfully');
        this.lessons = replaceOrAppend(
          this.lessons!,
          result,
          (f) => f.id == result.id,
        );
        this.displayedLessons = replaceOrAppend(
          this.displayedLessons ?? [],
          result,
          (f) => f.id == result.id,
        );
        this.router.navigate([result.id], { relativeTo: this.route });
      }
    });
  }

  async onAddToGrade(lesson: Lesson) {
    if (!this.groupId) return;
    let dialogConfig = new MatDialogConfig();

    this.logger.debug(
      'Adding lesson to grade: ',
      JSON.stringify(lesson, null, 2),
    );

    dialogConfig.data = {
      title: 'Add Lesson to Grade',
      description: `Add lesson '${lesson.lessonName}' to grade`,
      action: 'Add',
      groupId: this.groupId,
      lesson: lesson,
    };
    const dialogRef = this.dialog.open(DlgAddToGradeComponent, dialogConfig);
    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success('Lesson added to grade successfully');
      }
      return;
    });
  }

  async onEditLesson(item: Lesson) {
    this.router.navigate([item.id, 'edit'], {
      relativeTo: this.route,
      state: { lesson: item },
    });
  }

  onToggleLessonStatus(item: Lesson) {
    return this.store.dispatch(
      lessonActions.update({
        groupId: item.groupId!,
        lessonId: item.id!,
        lesson: {
          lessonStatus: item.lessonStatus,
        },
      }),
    );
  }

  onFilter() {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Filter Lessons',
      description: `Filter lessons based on criteria`,
      action: 'Apply Filters',
      groupId: this.groupId,
    };
    const dialogRef = this.dialog.open(
      DlgLessonsFiltersComponent,
      dialogConfig,
    );
    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success('Filters applied successfully');
      }
      return;
    });
  }

  clearGradeFilters() {
    this.searchGrades.clear();
    const formArray = this.advSearchForm.get('selectedGrades') as FormArray;
    formArray.controls.forEach((control) => {
      control.setValue(false);
    });
    this.displayedLessons = this.lessons;
  }

  onSelectGrade(event: Event, grade: Grade) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.searchGrades.add(grade.id!);
    } else {
      this.searchGrades.delete(grade.id!);
    }

    if (this.searchGrades.size === 0) {
      this.displayedLessons = this.lessons;
      return;
    }

    this.displayedLessons = this.lessons?.filter((lesson) =>
      this.searchGrades.has(lesson.gradeId!),
    );
  }

  isGradeSelected(grade: Grade): boolean {
    return this.searchGrades.has(grade.id!);
  }

  onDeleteLesson(item: Lesson) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Delete Lesson',
      description: `Are you sure you want to delete lesson '${item.lessonName}'?`,
      action: 'Delete',
      buttonColor: 'bg-red-500',
      showType: AlertType.error,
    };

    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result) => {
      if (result && item.groupId) {
        return this.store.dispatch(
          lessonActions.deleteAction({ groupId: item.groupId, id: item.id! }),
        );
      }
      return;
    });
  }

  async onSearchTermChange(value: string) {
    this.displayedLessons = this.lessons?.filter((item) =>
      item.lessonName?.toLowerCase()?.includes(value.toLowerCase()),
    );
  }

  async onSearch(term: string) {
    if (!term || !term.trim() || !this.groupId) return;
    this.displayedLessons = [];
    this.searchedLessons = [];
    this.isSearching$ = of(true);
    const cleanTerm = term.trim();
    this.activeSearchParams = { searchTerm: cleanTerm };

    try {
      const result = await this.lessonService.searchLessons(
        cleanTerm,
        this.groupId,
        undefined,
        undefined,
        this.searchPageSize,
        0,
        this.searchPageSize,
      );
      this.searchedLessons = result;
      this.displayedLessons = result;
      this.hasMoreSearchResults = result.length >= this.searchPageSize;
      this.isSearching$ = of(false);
      this.showingSearchResults$ = of(true);
      this.isShowingSearchResults = true;
    } catch (error: any) {
      this.isSearching$ = of(false);
      this.hasMoreSearchResults = false;
      this.utilsService.onShowAlert({
        title: 'Search Error',
        message:
          error?.message ||
          error?.toString() ||
          'An error occurred during search. Please try again.',
        code: 'ERR_SEARCH_FAILED',
      });
    }
  }

  onCancelSearch() {
    this.isSearching$ = of(false);
    this.displayedLessons = this.lessons;
    this.showingSearchResults$ = of(false);
    this.isShowingSearchResults = false;
    this.searchedLessons = undefined;
    this.activeSearchParams = null;
    this.hasMoreSearchResults = false;
    this.advSearchForm.patchValue({ searchTerm: '' });
    this.advSearchForm
      .get('gradeAge')
      ?.patchValue({ fromAge: null, toAge: null });
    this.clearGradeFilters();
    if (this.listUtilsBar) {
      this.listUtilsBar.searchTerm = '';
    }
  }

  get isSearchFormEmpty(): boolean {
    const term = this.advSearchForm.get('searchTerm')?.value?.trim();
    const fromAge = this.advSearchForm.get('gradeAge.fromAge')?.value;
    const toAge = this.advSearchForm.get('gradeAge.toAge')?.value;
    const selectedGradesValues =
      (this.advSearchForm.get('selectedGrades')?.value as boolean[]) || [];
    const hasGrades = selectedGradesValues.some((val) => val);

    return !term && !fromAge && !toAge && !hasGrades;
  }

  getFilterSummary(): string {
    const term = this.advSearchForm.get('searchTerm')?.value || '';

    const selectedGradesValues =
      (this.advSearchForm.get('selectedGrades')?.value as boolean[]) || [];
    const selectedGradeNames = this.availableGrades
      .filter((_, index) => selectedGradesValues[index])
      .map((grade) => grade.gradeName)
      .filter((name): name is string => !!name)
      .join(', ');

    const fromAge = this.advSearchForm.get('gradeAge.fromAge')?.value;
    const toAge = this.advSearchForm.get('gradeAge.toAge')?.value;
    let ageSummary = '';
    if (fromAge !== null && fromAge !== undefined && fromAge !== '') {
      if (toAge !== null && toAge !== undefined && toAge !== '') {
        ageSummary = `Ages ${fromAge}-${toAge}`;
      } else {
        ageSummary = `Ages ${fromAge}+`;
      }
    } else if (toAge !== null && toAge !== undefined && toAge !== '') {
      ageSummary = `Ages up to ${toAge}`;
    }

    const parts = [];
    if (term) parts.push(`"${term}"`);
    if (selectedGradeNames) parts.push(`Grades: ${selectedGradeNames}`);
    if (ageSummary) parts.push(ageSummary);

    return parts.join(' | ');
  }

  onAdvancedSearch() {
    if (this.listUtilsBar) {
      this.listUtilsBar.searchTerm =
        this.advSearchForm.get('searchTerm')?.value || '';
    }
  }

  onCancelAdvancedSearch() {
    if (this.listUtilsBar) {
      this.listUtilsBar.searchTerm = this.isShowingSearchResults
        ? this.getFilterSummary()
        : this.advSearchForm.get('searchTerm')?.value || '';
    }
  }

  clearAllFilters() {
    this.advSearchForm.patchValue({ searchTerm: '' });
    this.advSearchForm
      .get('gradeAge')
      ?.patchValue({ fromAge: null, toAge: null });
    this.clearGradeFilters();
    if (this.listUtilsBar) {
      this.listUtilsBar.searchTerm = '';
    }
    this.isShowingSearchResults = false;
    this.showingSearchResults$ = of(false);
    this.searchedLessons = undefined;
    this.activeSearchParams = null;
    this.hasMoreSearchResults = false;
    this.displayedLessons = this.lessons;
  }

  applyLocalFilters() {
    if (!this.lessons) return;

    const term = (this.advSearchForm.get('searchTerm')?.value || '')
      .trim()
      .toLowerCase();
    const fromAge = this.advSearchForm.get('gradeAge.fromAge')?.value;
    const toAge = this.advSearchForm.get('gradeAge.toAge')?.value;

    const selectedGradesValues =
      (this.advSearchForm.get('selectedGrades')?.value as boolean[]) || [];
    const selectedGradeIds = this.availableGrades
      .filter((_, index) => selectedGradesValues[index])
      .map((grade) => grade.id!)
      .filter((id): id is string => !!id);

    this.displayedLessons = this.lessons.filter((lesson) => {
      // 1. Text filter
      if (
        term &&
        !lesson.lessonName?.toLowerCase().includes(term) &&
        !lesson.tags?.some((t) => t.toLowerCase().includes(term))
      ) {
        return false;
      }

      // 2. Age range filter
      if (fromAge !== null && fromAge !== undefined && fromAge !== '') {
        const from = Number(fromAge);
        if (
          lesson.avgAge === undefined ||
          lesson.avgAge === null ||
          lesson.avgAge < from
        ) {
          return false;
        }
      }
      if (toAge !== null && toAge !== undefined && toAge !== '') {
        const to = Number(toAge);
        if (
          lesson.avgAge === undefined ||
          lesson.avgAge === null ||
          lesson.avgAge > to
        ) {
          return false;
        }
      }

      // 3. Grade ID filter
      if (selectedGradeIds.length > 0) {
        if (!lesson.gradeId || !selectedGradeIds.includes(lesson.gradeId)) {
          return false;
        }
      }

      return true;
    });
  }

  onRefresh() {
    this.lessonService
      .getLessons({ groupId: this.groupId! })
      .subscribe((lessons) => {
        this.lessons = this.displayedLessons = lessons;
        this.hasMoreLessons = lessons.length >= 10;
      });
  }

  toggleDropdown(lessonId: string, event: any) {
    event.preventDefault();
    event.stopPropagation();
    if (this.activeDropdownLessonId === lessonId) {
      this.activeDropdownLessonId = null;
    } else {
      this.activeDropdownLessonId = lessonId;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.activeDropdownLessonId = null;
  }
}
